import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Modal } from 'app-components';
import DatePicker from '../../../../lib/inputs/react-datepicker';
import Dropdown from '../../../../lib/inputs/Dropdown';
import CommonTextArea from '../../common/input/CommonTextArea';
import {
    EXCHANGE_ELIGIBILITY_OPTIONS,
    OVERRIDE_PROGRAM_ELIGIBILITY_TABLE_HEADER_DATA,
    CSR_ELIGIBILITY_OPTIONS,
    INPUT_MAX_LENGTH,
    ConditionalRendering,
    ValidationErrorMessage,
    INPUT_TYPES,
    NumberInput,
    OverrideProgramEligibilityModalPhaseType,
    ICsrOverideProgramEligibilityModalProps,
    IProgramEligibility,
    GIButton,
    aptcAmntValidate,
    eligibilityStartDateValidation,
    YesNo,
    PROGRAM_ELIGIBILITY_MODAL_PHASE,
    DEFAULT_PERSON_ID,
} from '../../utils';
import useApiHandling from '../../hooks/useApiHandling';
import { formatDate } from '../../../../utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY } from '../../../../utils/dates/dateFormats.utils';

const ModalContainer = styled.div`
    .gi-modal__content {
        max-width: none;
    }
    .gi-modal__body {
        border-bottom: 30px;
    }
`;

const StyledModal = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .f-center {
        display: flex;
        align-items: center;
        column-gap: 10px;
    }

    .gap-2 {
        display: flex;
        gap: 10px;
    }
`;

const CommonData = styled.div`
    display: flex;
    width: 100%;
    .w-50p {
        width: 50%;
    }
    .error--msg {
        margin: 0 !important;
    }
    @media (max-width: 1087px) {
        flex-direction: column;
        .w-50p {
            width: 100%;
        }
    }
`;

const Label = styled.p`
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.gray3};
    text-transform: none;
    letter-spacing: 0rem;
    padding-top: 4px;
    padding-bottom: 4px;
`;

const StyledField = styled.div`
    display: 'flex';
    gap: '20px';

    .date-picker,
    .shown.error--msg {
        padding: 8px;
        background-color: ${({ theme }) => theme.dangerLightest};
    }
    @media (max-width: 641px) {
        flex-direction: column;
        justify-content: center;
    }

    .gi-input {
        text-align: left;
        margin: 0;
    }
`;

const OverrideTable = styled.table`
    width: 100%;
    margin-top: 24px;
    .pr-10 {
        padding-right: 10px;
    }
    span {
        color: ${({ theme }) => theme.dangerMain};
        margin-left: 5px;
    }
`;
const Td = styled.td`
    padding-right: 10px;
    .gi-dropdown {
        align-items: flex-start;
        flex-direction: column;
        text-align: left;
        label {
            display: block;
        }
    }
    .p-14-0 {
        padding: 14px 0;
    }
`;

const StyledDropdown = styled.div`
    .gi-dropdown__select {
        width: ${({ width }) => width};
    }
`;
const InputContainer = styled.div`
    margin-top: 20px;
    label {
        font-size: 16px;
        color: ${({ theme }) => theme.infoMain};
        margin-bottom: 5px;
        display: block;

        span {
            color: ${({ theme }) => theme.dangerMain};
            margin-left: 5px;
        }
    }
`;

const StyledNumberInput = styled.div`
    display: 'flex';
    gap: '20px';
`;

const CsrOverrideProgramEligibilityModal: React.FC = ({
    closeModal,
    data,
    application,
}: ICsrOverideProgramEligibilityModalProps) => {
    const [overrideEligibilityData, setOverrideEligibilityData] = useState<IProgramEligibility>({
        ...data,
        eligibilityStartDate: new Date(data.eligibilityStartDate),
    });
    const [error, setError] = useState({
        aptcZeroAmntErrorFlag: false,
        aptcMaxAmntErrorFlag: false,
        isNan: false,
        differentYear: false,
        invalidStartDate: false,
    });
    const [confirmationText, setConfirmationText] = useState('');
    const [overrideProgramEligibilityModalPhase, setOverrideProgramEligibilityModalPhase] =
        useState<OverrideProgramEligibilityModalPhaseType>('override');
    const { submitOverrideProgElig } = useApiHandling();
    const isModalPhaseOverride = overrideProgramEligibilityModalPhase === PROGRAM_ELIGIBILITY_MODAL_PHASE;
    const isFinancialAssistanceFlag = overrideEligibilityData.financialAssistanceFlag === YesNo.Y;

    useEffect(() => {
        aptcAmntValidate(overrideEligibilityData.applicantEligibility, overrideEligibilityData.maximumAPTC, setError);
    }, [overrideEligibilityData.maximumAPTC]);

    useEffect(() => {
        eligibilityStartDateValidation(
            overrideEligibilityData.eligibilityStartDate,
            application.coverageYear,
            setError,
        );
    }, [overrideEligibilityData.eligibilityStartDate]);

    const handleDateChange = value => {
        setOverrideEligibilityData({ ...overrideEligibilityData, eligibilityStartDate: value });
    };

    function onClose() {
        closeModal('csrOverrideProgramEligibilityModal');
    }

    function onSubmit() {
        if (isModalPhaseOverride) setOverrideProgramEligibilityModalPhase('confirmation');
        else {
            submitOverrideProgElig(
                {
                    ...overrideEligibilityData,
                    eligibilityStartDate: formatDate(
                        overrideEligibilityData.eligibilityStartDate,
                        MM_SLASH_DD_SLASH_YYYY,
                    ),
                },
                confirmationText,
                application.coverageYear,
                application.caseNumber,
            );
            onClose();
        }
    }

    function onBack() {
        setOverrideProgramEligibilityModalPhase('override');
    }

    const handleChangeTableData = (index, key, value) => {
        const overridenData = overrideEligibilityData.applicantEligibility?.map((item, i) =>
            index === i ? { ...item, [key]: value } : { ...item },
        );
        setOverrideEligibilityData({ ...overrideEligibilityData, applicantEligibility: overridenData });
        if (key === 'aptcEligibility') {
            aptcAmntValidate(overridenData, overrideEligibilityData.maximumAPTC, setError);
        }
    };

    return (
        <ModalContainer>
            <Modal id="override_program_eligibility_modal" classNames="maxw-none">
                <Modal.Header
                    id="override_program_eligibility_modal_title"
                    titleId={
                        isModalPhaseOverride
                            ? 'cap.applications.modal.overrideProgramEligibility.title'
                            : 'cap.applications.modal.overrideProgramEligibility.title2'
                    }
                    onHide={onClose}
                />
                <Modal.Body>
                    <ConditionalRendering isTrue={isModalPhaseOverride}>
                        <p data-testid="override_program_eligibility_modal_body_text">
                            <FormattedMessage id="cap.application.modal.overrideProgramEligibilityAlert.text" />
                        </p>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={!isModalPhaseOverride}>
                        <p data-testid="override_program_eligibility_modal_body_text">
                            <FormattedMessage id="cap.application.modal.confirmProgramEligibilityAlert.text" />
                        </p>
                    </ConditionalRendering>
                    <CommonData>
                        <ConditionalRendering isTrue={isFinancialAssistanceFlag}>
                            <div className="w-50p">
                                <Label data-testid="household_maximum_aptc_amount_label">
                                    <FormattedMessage id="cap.application.modal.overrideProgramEligibility.commonData.label.householdMaximumAPTCAmount" />
                                </Label>
                                <StyledNumberInput>
                                    <NumberInput
                                        id="household_maximum_aptc_amount_input"
                                        value={overrideEligibilityData?.maximumAPTC?.toString() || ''}
                                        setValue={value => {
                                            setOverrideEligibilityData({
                                                ...overrideEligibilityData,
                                                maximumAPTC: value,
                                            });
                                        }}
                                        hasError={error.isNan}
                                        disabled={!isModalPhaseOverride}
                                    />
                                    <ValidationErrorMessage
                                        isVisible={error.aptcMaxAmntErrorFlag}
                                        messageText={
                                            'cap.application.modal.programEligibilityEnterDate.maxValidAptcAmount'
                                        }
                                    />
                                    <ValidationErrorMessage
                                        isVisible={error.aptcZeroAmntErrorFlag}
                                        messageText={
                                            'cap.application.modal.programEligibilityEnterDate.aptAmountShouldBeZero'
                                        }
                                    />
                                </StyledNumberInput>
                            </div>
                        </ConditionalRendering>
                        <div className="w-50p">
                            <Label data-testid="eligibility_label">
                                <FormattedMessage id="cap.application.modal.overrideProgramEligibility.commonData.label.eligibility" />
                                <ConditionalRendering isTrue={isFinancialAssistanceFlag}>
                                    <span>
                                        <FormattedMessage id="cap.application.modal.overrideProgramEligibility.commonData.label.aptc&csr" />
                                    </span>
                                </ConditionalRendering>
                                <FormattedMessage id="cap.application.modal.overrideProgramEligibility.commonData.label.startDate" />
                            </Label>
                            <div>
                                <StyledField>
                                    <DatePicker
                                        labelText=""
                                        wrapperClasses={['date-picker']}
                                        isRequired
                                        id="dob"
                                        type="text"
                                        pattern="MM/DD/YYYY"
                                        disabled={!isModalPhaseOverride}
                                        value={overrideEligibilityData?.eligibilityStartDate}
                                        withMask={true}
                                        onDateChange={value => handleDateChange(value || '')}
                                    />
                                    <ValidationErrorMessage
                                        isVisible={!overrideEligibilityData?.eligibilityStartDate}
                                        messageText={
                                            'cap.application.modal.programEligibilityEnterDate.enterEligibilityStartDate'
                                        }
                                    />
                                    <ValidationErrorMessage
                                        isVisible={
                                            overrideEligibilityData?.eligibilityStartDate && error.invalidStartDate
                                        }
                                        messageText={
                                            'cap.application.modal.programEligibilityEnterDate.enterValidStartDate'
                                        }
                                    />
                                    <ValidationErrorMessage
                                        isVisible={
                                            overrideEligibilityData?.eligibilityStartDate &&
                                            !error.invalidStartDate &&
                                            error?.differentYear
                                        }
                                        messageText={
                                            'cap.application.modal.programEligibilityEnterDate.sepEnrollmentPeriods'
                                        }
                                        textReplacements={application.coverageYear}
                                    />
                                </StyledField>
                            </div>
                        </div>
                    </CommonData>
                    <OverrideTable>
                        <tr>
                            {OVERRIDE_PROGRAM_ELIGIBILITY_TABLE_HEADER_DATA.map(item => (
                                <ConditionalRendering
                                    isTrue={item.renderConditionally ? isFinancialAssistanceFlag : true}
                                >
                                    <th className="pr-10" data-testid={item.id}>
                                        <FormattedMessage id={item.text} />
                                    </th>
                                </ConditionalRendering>
                            ))}
                        </tr>
                        {overrideEligibilityData.applicantEligibility.map((applicant, index) => {
                            return (
                                <tr>
                                    <Td data-testid={`applicant_name_${applicant.id}`}>
                                        {`${applicant.firstName} ${applicant.middleName ? applicant.middleName : ''} ${
                                            applicant.lastName
                                        }`}
                                        <ConditionalRendering isTrue={applicant.personId === DEFAULT_PERSON_ID}>
                                            {' '}
                                            (<FormattedMessage id={'cap.applications.modal.primaryText'} />)
                                        </ConditionalRendering>
                                    </Td>
                                    <Td>
                                        <ConditionalRendering isTrue={isModalPhaseOverride}>
                                            <StyledDropdown
                                                width={'120px'}
                                                data-testid={`exchange_eligibility_dropdown_${applicant.id}`}
                                            >
                                                <Dropdown
                                                    label={{
                                                        labelText: '',
                                                    }}
                                                    inputData={{
                                                        inputClasses: ['gi-dropdown__select grid-col-4'],
                                                        name: 'exchange_eligibility',
                                                        options: EXCHANGE_ELIGIBILITY_OPTIONS.map(v => ({
                                                            value: v,
                                                            text: v,
                                                        })),
                                                        type: INPUT_TYPES.DROPDOWN,
                                                        value: applicant.exchangeEligibility,
                                                        isInputRequired: false,
                                                    }}
                                                    inputActions={{
                                                        handleFieldChange: val => {
                                                            handleChangeTableData(index, 'exchangeEligibility', val);
                                                        },
                                                    }}
                                                />
                                            </StyledDropdown>
                                        </ConditionalRendering>
                                        <ConditionalRendering isTrue={!isModalPhaseOverride}>
                                            <div className="p-14-0">{applicant.exchangeEligibility}</div>
                                        </ConditionalRendering>
                                    </Td>
                                    <ConditionalRendering isTrue={isFinancialAssistanceFlag}>
                                        <Td>
                                            <ConditionalRendering isTrue={isModalPhaseOverride}>
                                                <StyledDropdown
                                                    width={'120px'}
                                                    data-testid={`aptc_eligibility_dropdown_${applicant.id}`}
                                                >
                                                    <Dropdown
                                                        label={{
                                                            labelText: '',
                                                            labelClass: [''],
                                                        }}
                                                        inputData={{
                                                            inputClasses: ['gi-dropdown__select grid-col-4'],
                                                            name: 'aptc_eligibility',
                                                            options: EXCHANGE_ELIGIBILITY_OPTIONS.map(v => ({
                                                                value: v,
                                                                text: v,
                                                            })),
                                                            type: INPUT_TYPES.DROPDOWN,
                                                            value: applicant.aptcEligibility,
                                                            isInputRequired: false,
                                                        }}
                                                        inputActions={{
                                                            handleFieldChange: val => {
                                                                handleChangeTableData(index, 'aptcEligibility', val);
                                                            },
                                                        }}
                                                    />
                                                </StyledDropdown>
                                            </ConditionalRendering>
                                            <ConditionalRendering isTrue={!isModalPhaseOverride}>
                                                <div className="p-14-0">{applicant.aptcEligibility}</div>
                                            </ConditionalRendering>
                                        </Td>
                                    </ConditionalRendering>
                                    <Td>
                                        <ConditionalRendering isTrue={isModalPhaseOverride}>
                                            <StyledDropdown
                                                width={'180px'}
                                                data-testid={`csr_eligibility_dropdown_${applicant.id}`}
                                            >
                                                <Dropdown
                                                    label={{
                                                        labelText: '',
                                                    }}
                                                    inputData={{
                                                        inputClasses: ['gi-dropdown__select grid-col-4'],
                                                        name: 'csr_eligibility',
                                                        options: Object.values(CSR_ELIGIBILITY_OPTIONS).map(v => v),
                                                        type: INPUT_TYPES.DROPDOWN,
                                                        value: applicant.csrLevel,
                                                        isInputRequired: false,
                                                    }}
                                                    inputActions={{
                                                        handleFieldChange: val => {
                                                            handleChangeTableData(index, 'csrLevel', val);
                                                        },
                                                    }}
                                                />
                                            </StyledDropdown>
                                        </ConditionalRendering>
                                        <ConditionalRendering isTrue={!isModalPhaseOverride}>
                                            <div className="p-14-0">
                                                {CSR_ELIGIBILITY_OPTIONS[applicant.csrLevel]?.text}
                                            </div>
                                        </ConditionalRendering>
                                    </Td>
                                    <ConditionalRendering isTrue={isFinancialAssistanceFlag}>
                                        <Td data-testid={`medicaid_eligibility_dropdown_${applicant.id}`}>
                                            {applicant.medicaidEligibility}
                                        </Td>
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={isFinancialAssistanceFlag}>
                                        <Td data-testid={`chip_eligibility_dropdown_${applicant.id}`}>
                                            {applicant.chipEligibility}
                                        </Td>
                                    </ConditionalRendering>
                                </tr>
                            );
                        })}
                    </OverrideTable>

                    <ConditionalRendering isTrue={!isModalPhaseOverride}>
                        <InputContainer>
                            <label>
                                <FormattedMessage id="cap.application.modal.overrideProgramEligibility.confirmationPhase.label" />
                                . <span>*</span>
                            </label>
                            <CommonTextArea
                                id="override_program_eligibility_modal_textarea"
                                value={confirmationText}
                                setValue={(value: string) =>
                                    value.length <= INPUT_MAX_LENGTH && setConfirmationText(value)
                                }
                                placeholder={'member.enrollment.modal.textarea.placeholder'}
                                style={{ height: '80px' }}
                            />
                            <div>
                                {INPUT_MAX_LENGTH - confirmationText.trim().length}{' '}
                                <FormattedMessage id="cap.applications.modal.charactersLeft" />
                            </div>
                        </InputContainer>
                    </ConditionalRendering>
                </Modal.Body>
                <Modal.Controls>
                    <StyledModal>
                        <div className="gap-2">
                            <ConditionalRendering isTrue={!isModalPhaseOverride}>
                                <GIButton
                                    id="override_program_eligibility_modal_back_button"
                                    labelId="cap.applications.modal.button.label.back"
                                    onClickHandler={onBack}
                                ></GIButton>
                            </ConditionalRendering>
                            <GIButton
                                id="override_program_eligibility_modal_continue_button"
                                labelId="cap.applications.modal.button.label.continue"
                                onClickHandler={onSubmit}
                                isDisabled={
                                    isModalPhaseOverride ? Object.values(error).some(Boolean) : !confirmationText.length
                                }
                            ></GIButton>
                            <GIButton
                                id="override_program_eligibility_modal_cancel_button"
                                labelId="cap.applications.modal.button.label.cancel"
                                variant="secondary"
                                onClickHandler={onClose}
                            ></GIButton>
                        </div>
                    </StyledModal>
                </Modal.Controls>
            </Modal>
        </ModalContainer>
    );
};

export default CsrOverrideProgramEligibilityModal;
